import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Settings, MoveUp, MoveDown, Trash2, Copy, Maximize2 } from 'lucide-react';

interface EditorOverlayProps {
  sectionId: string;
  children: React.ReactNode;
  onSelect: (sectionId: string) => void;
  onMoveUp?: (sectionId: string) => void;
  onMoveDown?: (sectionId: string) => void;
  onDelete?: (sectionId: string) => void;
  onDuplicate?: (sectionId: string) => void;
}

export const EditorOverlay: React.FC<EditorOverlayProps> = ({ 
  sectionId, 
  children, 
  onSelect,
  onMoveUp,
  onMoveDown,
  onDelete,
  onDuplicate
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsPreview(params.get('preview') === 'true');

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'SECTION_SELECTED' && event.data.sectionId === sectionId) {
        setIsSelected(true);
      } else if (event.data.type === 'SECTION_SELECTED') {
        setIsSelected(false);
      }
    };

    const handleClickOutside = () => setShowContextMenu(false);

    window.addEventListener('message', handleMessage);
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('click', handleClickOutside);
    };
  }, [sectionId]);

  if (!isPreview) return <>{children}</>;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(sectionId);
    window.parent.postMessage({ type: 'SELECT_SECTION', sectionId }, '*');
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleAction = (type: string, direction?: 'up' | 'down') => {
    setShowContextMenu(false);
    if (type === 'move') {
      if (direction === 'up') {
        onMoveUp?.(sectionId);
        window.parent.postMessage({ type: 'MOVE_SECTION', sectionId, direction: 'up' }, '*');
      } else {
        onMoveDown?.(sectionId);
        window.parent.postMessage({ type: 'MOVE_SECTION', sectionId, direction: 'down' }, '*');
      }
    } else if (type === 'duplicate') {
      onDuplicate?.(sectionId);
      window.parent.postMessage({ type: 'DUPLICATE_SECTION', sectionId }, '*');
    } else if (type === 'delete') {
      onDelete?.(sectionId);
      window.parent.postMessage({ type: 'DELETE_SECTION', sectionId }, '*');
    } else if (type === 'select') {
      onSelect(sectionId);
      window.parent.postMessage({ type: 'SELECT_SECTION', sectionId }, '*');
    }
  };

  return (
    <div 
      className={`relative group/editor transition-all duration-300 ${isSelected ? 'ring-2 ring-brand-500 ring-inset' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {/* Hover Overlay */}
      <AnimatePresence>
        {(isHovered || isSelected) && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-brand-500/5 pointer-events-none z-[40] border-2 border-brand-500/30 border-dashed"
          />
        )}
      </AnimatePresence>

      {/* Toolbar */}
      <AnimatePresence>
        {(isHovered || isSelected) && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-4 right-4 z-[50] flex items-center gap-1 bg-zinc-900 border border-white/10 rounded-lg p-1 shadow-2xl backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-2 py-1 border-r border-white/10 mr-1">
              <span className="text-[9px] font-bold uppercase tracking-widest text-brand-500">{sectionId}</span>
            </div>
            
            <button 
              onClick={() => handleAction('move', 'up')}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-all"
              title="Move Up"
            >
              <MoveUp size={14} />
            </button>
            <button 
              onClick={() => handleAction('move', 'down')}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-all"
              title="Move Down"
            >
              <MoveDown size={14} />
            </button>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <button 
              onClick={() => handleAction('duplicate')}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/5 rounded-md transition-all"
              title="Duplicate"
            >
              <Copy size={14} />
            </button>
            <button 
              onClick={() => handleAction('select')}
              className="p-1.5 text-zinc-400 hover:text-brand-500 hover:bg-brand-500/5 rounded-md transition-all"
              title="Edit Settings"
            >
              <Settings size={14} />
            </button>
            <button 
              onClick={() => handleAction('delete')}
              className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-500/5 rounded-md transition-all"
              title="Delete Section"
            >
              <Trash2 size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Context Menu */}
      <AnimatePresence>
        {showContextMenu && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed z-[100] bg-zinc-900 border border-white/10 rounded-xl shadow-2xl py-2 min-w-[200px] backdrop-blur-xl"
            style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <ContextMenuItem icon={<Settings size={14} />} label="Edit Section" onClick={() => handleAction('select')} />
            <ContextMenuItem icon={<Copy size={14} />} label="Duplicate" onClick={() => handleAction('duplicate')} />
            <div className="h-[1px] bg-white/5 my-1" />
            <ContextMenuItem icon={<MoveUp size={14} />} label="Move Up" onClick={() => handleAction('move', 'up')} />
            <ContextMenuItem icon={<MoveDown size={14} />} label="Move Down" onClick={() => handleAction('move', 'down')} />
            <div className="h-[1px] bg-white/5 my-1" />
            <ContextMenuItem icon={<Trash2 size={14} />} label="Delete Section" className="text-red-500" onClick={() => handleAction('delete')} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resize Handles (Visual only for now) */}
      {(isHovered || isSelected) && (
        <>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-brand-500 rounded-full z-[51] shadow-[0_0_10px_rgba(54,180,253,0.5)]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-500 rounded-full z-[51] shadow-[0_0_10px_rgba(54,180,253,0.5)]" />
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 bg-brand-500 rounded-full z-[51] shadow-[0_0_10px_rgba(54,180,253,0.5)]" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-2 bg-brand-500 rounded-full z-[51] shadow-[0_0_10px_rgba(54,180,253,0.5)]" />
        </>
      )}

      {children}
    </div>
  );
};

const ContextMenuItem = ({ icon, label, onClick, className = "" }: { icon: React.ReactNode, label: string, onClick: () => void, className?: string }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2 text-xs text-zinc-400 hover:text-white hover:bg-white/5 transition-all text-left ${className}`}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);
